<template>
  <div class="opc-capacity-config-row opc-capacity-config-row-v2">
    <div class="opc-capacity-config-row__license">
      <div class="opc-capacity-config-row__license__content">
        <div class="opc-capacity-config-row__license__content--cycle">
          <!-- Capacity Section -->
          <div class="opc-capacity-config-row__capacity">
            <!-- Text View Model -->
            <config-text-row
              v-if="textViewModel"
              :title="capitalizeFirstLetter($t(capacityUnitI18nKey))"
              :value="selectCapacity"
            />

            <!-- Capacity Dropdown -->
            <div v-else-if="showSelectCapacityDropdown" class="opc-capacity-config-row__capacity--wrap">
              <span>{{capitalizeFirstLetter($t(capacityUnitI18nKey))}}</span>
              <zm-select
                data-testid="checkout-select-11"
                ref="attendeeSelect"
                :size="selectCapacitySize"
                v-model="selectedValue"
                :aria-label="selectLabel || `${planName} ${$t(capacityUnitI18nKey)}`"
                @update:model-value="onSwitchCapacity"
                floating-content-class="opc-capacity-config-row__capacity--popper"
                class="opc-capacity-config-row__capacity--select"
              >
                <zm-select-option-group
                  v-for="group in capacityGroupOptions.filter(group => group?.options?.length)"
                  :key="group.label"
                  :label="group.label"
                >
                  <zm-select-option
                    v-for="p in setCapacityOptions(group.options)"
                    :key="combineCapacity(planType, p.capacity, group.cycle)"
                    :value="combineCapacity(planType, p.capacity, group.cycle)"
                    :label="capacityFormatter(p.capacity)"
                    :disabled="p.disabled"
                    :tooltip-content="p.tooltips"
                  />
                </zm-select-option-group>
              </zm-select>
            </div>

            <!-- Capacity View -->
            <div v-else class="opc-capacity-config-row__cycle__title">
              {{ selectCapacity }} <span class="opc-capacity-config-row__capacity--unit">{{ $t(capacityUnitI18nKey) }}</span>
            </div>

            <!-- Capacity Tips -->
            <slot v-if="$slots.capacityTips" name="capacityTips" />
            <zm-tooltip
              v-else-if="capacityTips"
              class="opc-capacity-config-row__tooltips"
              role="button"
              disallow-learn-about
              aria-label="Learn more about Select the number of licenses"
              :content="capacityTips"
              placement="bottom"
              @input="toggleCapacityTips"
            >
              <tips-icon type="info" aria-label="Learn more about Select the number of licenses" />
            </zm-tooltip>
          </div>

          <!-- Cycle Row -->
          <config-cycle
            ref="configCycle"
            :billing-cycle="billingCycle"
            :billing-cycle-list="billingCycleList"
            :billing-cycle-group-options="billingCycleGroupOptions.filter(group => group?.options?.length)"
            :show-monthly-price-from-cycle="showMonthlyPriceFromCycle"
            :plan-name="planName"
            :text-view-model="isEventSaasOrEventSassCore(planType, false) && billingCycleGroupOptions.filter(group => group?.options?.length).length <= 1"
            @switch-cycle-action="switchBillingCycle"
            @sync-cycle="changeBillingCycle"
          />
        </div>

        <!-- License Section -->
        <div class="opc-capacity-config-row__input__number">
          <input-number-with-popover
            :modelValue="license"
            @update:modelValue="changeLicense"
            size="lg"
            controls-position="both"
            :min="minHosts"
            :max="maxHosts"
            :increase-tips="getIncreaseTips(selectCapacity, license)"
            :decrease-tips="decreaseTips"
            :unit="inputUnit"
            :label="`${trackingPlanKey} + Select the number of ${planUnit}`"
            :data-testid="`checkout-input-license-${selectedPlanCode}`"
          />

          <!-- License Tips -->
          <slot v-if="$slots.licenseTips" name="licenseTips" />
          <zm-tooltip
            v-else-if="licenseTips"
            class="opc-capacity-config-row__tooltips"
            role="button"
            disallow-learn-about
            aria-label="Learn more about Select the number of licenses"
            :content="licenseTips"
            placement="bottom"
            @input="toggleLicenseTips"
          >
            <tips-icon type="info" aria-label="Learn more about Select the number of licenses" />
          </zm-tooltip>
        </div>
      </div>

      <!-- Remove Button -->
      <zm-button
        data-testid="checkout-button-12"
        type="link"
        ghost
        size="mini"
        @click="onDelete"
        :aria-label="`remove ${trackingPlanKey}`"
        :class="{
          'opc-capacity-config-row__delete-btn': true,
          'opc-capacity-config-row__delete--btn': !deleteVisible,
        }"
      >
        <i class="opc-capacity-config-row__delete--icon zm-icon-close" />
      </zm-button>
    </div>

    <!-- Amount Section -->
    <p class="opc-cycle__plan__price">
      <span class="opc-cycle__plan__price--amount">
        {{ amountInfo.amount }}
      </span>
      <span class="opc-cycle__plan__price--unit">
        {{ amountInfo.cycleLabel }}<price-v-a-t-tip />
      </span>
    </p>

    <!-- License Alert -->
    <slot v-if="$slots.licenseAlert" name="licenseAlert" />
    <zm-banner show-close
      v-else-if="licenseAlert"
      class="opc-common-info-alert"
      type="warning"
      show-icon
    >
      <p v-html="licenseAlert" @click="trackingAlert" />
    </zm-banner>

    <!-- Cycle Disabled Alert -->
    <slot v-if="$slots.cycleAlert && showCycleAlert && firstDisabledCycleAlertItem" name="cycleAlert" />
    <zm-banner show-close
      v-else-if="showCycleAlert && firstDisabledCycleAlertItem?.alert"
      class="opc-common-info-alert"
      type="warning"
      show-icon
    >
      <p v-html="firstDisabledCycleAlertItem.alert" @click="trackingAlert" />
    </zm-banner>

    <!-- Divider -->
    <hr v-if="dividerVisible" />
  </div>
</template>

<script>
import _ from 'lodash';
import { mapActions, mapMutations, mapGetters } from 'vuex';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover';
import {
  BILLING_CYCLE, isEventSaasOrEventSassCore,
  planTypes,
  TrackingBillingCycleKeys, TrackingBillingCycleUnitKeys
} from '@/common/constants';
import { isEmptyVal, noopPromise } from '@/utils';
import ConfigCycleV2 from '@/components/service/config-billing-cycle/v2/index';
import { isAutoAddable } from '@/plans/ZOOM_MEETING/common/utils';
import { showSyncAddonMessage } from '@/common/mixin/autoAdd';
import { capacityFormatter, capitalizeFirstLetter } from '@/common/utils';
import { getPlanUnitWithCycle, planUnitMap } from '@/common/product-unit';
import ConfigTextRow from '@/plans/EVENT_SAAS/component/config-text-row.vue';
import { BillingCycleKeysMapForAx } from '@/components/service/utils';
import { getTrackingProductNameByPlanType } from '@/aspects/trackings';

// @V3-TODO @Adam Mao > check deleted methods & props
export const PLAN_UNIT_MAP = {
  license: 'license',
  user: 'user',
};

const splitCapacity = (value) => {
  const values = value?.split?.('-');
  if (values?.length) {
    const [capacity, billingCycle] = values;
    return [Number(capacity), billingCycle];
  }
  return [];
};

const combineCapacity = (planType, capacity, cycle) => {
  if (isEventSaasOrEventSassCore(planType, false)) {
    return `${capacity}-${cycle}`;
  }
  return capacity;
}

const setCapacityOptions = (options) => {
  if (Array.isArray(options)) {
    return options.map(o => {
      if (typeof o === 'number') {
        return {
          capacity: o,
        }
      }
      return o;
    })
  }
  return [options];
}

export default {
  name: 'ConfigRowV2',
  components: {
    ConfigCycle: ConfigCycleV2,
    InputNumberWithPopover,
    ConfigTextRow,
  },
  props: {
    planType: String,
    // Current license count for downsell calculation
    currentLicense: Number,
    // show alert warning or tooltips
    // true -> alert warning
    // false -> tooltips
    showCycleAlert: Boolean,
    showMonthlyPriceFromCycle: Boolean,
    // weather or not show capacity dropdown list
    showSelectCapacityDropdown: {
      type: Boolean,
      default: true,
    },
    capacityTips: String,
    licenseTips: String,
    selectCapacitySize: {
      type: String,
      default: 'lg',
    },
    capacityUnitI18nKey: {
      type: String,
      default: 'billing.participants',
    },
    // plan unit (license/user/line)
    planUnit: {
      type: String,
      default: PLAN_UNIT_MAP.license,
      validator(value) {
        return Object.keys(PLAN_UNIT_MAP).includes(value);
      },
    },
    // current show capacity
    selectCapacity: {
      type: Number,
      required: true,
    },
    // capacity dropdown list
    capacityList: {
      type: Array,
      required: true,
      default: () => [],
    },
    // billing cycle plan list
    billingCycleList: {
      type: Array,
      required: true,
      default: () => [],
    },
    // capacity delete cta
    deleteVisible: {
      type: Boolean,
      default: true,
    },
    dividerVisible: {
      type: Boolean,
      default: true,
    },
    maxHosts: {
      type: Number,
      default: 999,
    },
    minHosts: {
      type: Number,
      default: 1,
    },
    decreaseTips: String,
    licenseAlert: String,
    beforeSwitchCycle: {
      type: Function,
      default: noopPromise,
    },
    getIncreaseTips: {
      type: Function,
      default: () => {
      },
    },
    unit: {
      type: Number,
      default: planUnitMap.license,
      validator: function(v) {
        return _.chain(planUnitMap)
          .values()
          .includes(v)
          .value();
      }
    },
    selectLabel: {
      type: String,
      default: '',
    },
    capacityGroupOptions: {
      type: Object,
      default: () => ({})
    },
    billingCycleGroupOptions: {
      type: Object,
      default: () => ({})
    },
    textViewModel: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isEventSaasOrEventSassCore,
      license: 1,
      billingCycle: '',
    };
  },
  computed: {
    ...mapGetters([
      'syncBaseToSelectedPlanLicense',
      'getCurrentMeetingPlans',
      'getCurrentBasePlanInfo',
      'getSelectedPlans',
      'planMapFlat'
    ]),
    currentPlanList() {
      return _.map(this.billingCycleList, (p) => p.plan);
    },
    currentGroupPlanList() {
      return this.billingCycleGroupOptions.map(plan => {
        return plan.options.map(p => p.plan);
      }).flat();
    },
    cartSelectedPlan() {
      return _.find(this.currentPlanList, (p) => p?.cartInfo?.selected_number > 0);
    },
    trackingPlanInfo() {
      const planName = getTrackingProductNameByPlanType(this.planType);
      return {
        planName: planName,
        billingCycle: TrackingBillingCycleKeys[this.billingCycle],
        buySelection: `${TrackingBillingCycleKeys[this.billingCycle]} - ${this.selectCapacity} ${this.$tEn(this.capacityUnitI18nKey)}`
      }
    },
    planName() {
      return getTrackingProductNameByPlanType(this.planType);
    },
    trackingPlanKey() {
      return `${this.planName} ${this.selectCapacity ?? this.$tEn(this.capacityUnitI18nKey)} `;
    },
    selectedPlanCode() {
      return _.find(this.currentPlanList, (p) => p.billing_cycle === this.billingCycle)?.plan_code;
    },
    inputUnit() {
      return this.$t(`billing.planform.unit.${this.planUnit}`);
    },
    currentBasePlan() {
      const basePlans = this.getCurrentMeetingPlans.length ? this.getCurrentMeetingPlans : this.getCurrentBasePlanInfo?.plans;
      return _.get(basePlans, ['0'], {});
    },
    amountInfo() {
      const { amount = 0, billing_cycle, esmd_amount = 0 } = this?.cartSelectedPlan ?? {};

      // Check if we should use downsell amount - use passed props
      let effectiveAmount = amount;
      if (this.cartSelectedPlan?.isDownsellAmountOverwrite &&
          this.cartSelectedPlan?.downsellAmount &&
          this.currentLicense !== undefined) {
        const newLicense = this.cartSelectedPlan?.cartInfo?.selected_number || 0;

        // If license is decreasing, use downsell amount
        if (newLicense < this.currentLicense && newLicense > 0) {
          effectiveAmount = this.cartSelectedPlan.downsellAmount;
        }
      }

      return {
        amount: this.$money(effectiveAmount + esmd_amount, { forceDiff: true }),
        cycleLabel: this.$t(billing_cycle ? getPlanUnitWithCycle(billing_cycle, this.unit) : '')
      };
    },
    allWebinarPlansList() {
      return this.planMapFlat.filter(p => p.type === this.planType);
    },
    selectedValue() {
      return combineCapacity(this.planType, this.selectCapacity, this.billingCycle);
    },
    firstDisabledCycleAlertItem() {
      return _.find(this.billingCycleList, (p) => p.disabled && !isEmptyVal(p.alert));
    },
  },
  methods: {
    ...mapActions(['switchCapacityAction']),
    ...mapMutations(['updateSelectPlan']),
    capacityFormatter,
    capitalizeFirstLetter,
    setCapacityOptions,
    combineCapacity,
    async switchBillingCycle(oldPlan, newPlan) {
      if (!newPlan) return;
      // when the current license less than max, get current license, otherwise get maxHosts
      const quantity = this.license <= this.maxHosts ? this.license : this.maxHosts;
      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      const preCartInfo = await this.beforeSwitchCycle(oldPlan, newPlanInfo);

      await this.switchCapacityAction([
        oldPlan?.plan_code,
        { ...newPlanInfo, cartInfo: { ...newPlanInfo.cartInfo, ...preCartInfo } },
      ]);

      this.updateVisuallyHiddenTextContent(newPlanInfo.billing_cycle, newPlanInfo.capacity);

      // event tracking
      const billingCycle = this.cartSelectedPlan?.billing_cycle;
      if (this.$context?.is.zs2ze) {
        billingCycle && this.$tracking.buyInteraction(`zoom events plan`, billingCycle);
      } else {
        billingCycle && this.$tracking.addOnBuyInteraction(
          `${this.trackingPlanKey} billing cycle`,
          this.$tEn(`billing.billing_cycle.${billingCycle}`)?.toLowerCase(),
        );
      }
    },
    updateCart(newLicense, oldLicense) {
      const planCode = this.selectedPlanCode;

      if (planCode) {
        this.updateSelectPlan({
          plan_code: planCode,
          cartInfo: { selected_number: newLicense },
        });
      }

      this.$nextTick(() => {
        // event tracking
        if (this.$context?.is.zs2ze) {
          planCode && this.$tracking.buyInteraction(`zoom events plan license`,
            `${this.billingCycle} - ${this.selectCapacity} attendees - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
        } else if ([planTypes.WEBINAR].includes(this.planType)) {
          planCode && this.$tracking.buyInteraction(`${this.trackingPlanInfo.planName}`,
            `${this.trackingPlanInfo.buySelection} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
        } else {
          planCode && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} license`,
            `${this.trackingPlanKey} - ${newLicense > oldLicense ? 'increase' : 'decrease'} - ${newLicense}`);
        }
      });
    },
    changeLicense(newLicense) {
      const oldLicense = this.license;
      this.license = newLicense;
      const payload = [newLicense, oldLicense];

      if (this.updateShoppingCart) {
        this.updateShoppingCart(...payload);
      } else {
        this.updateCart(...payload);
      }
    },
    onSwitchCapacity(capacity, billingCycle) {
      const isFromTabKeyboard = document.activeElement.tagName === 'DL' && document.activeElement.getAttribute('role') === 'listbox';
      const { planName } = this.trackingPlanInfo;
      const { planType, trackingPlanKey } = this;
      const [_capacity, _billingCycle] = splitCapacity(capacity);
      capacity = _capacity || capacity;
      billingCycle = _billingCycle || billingCycle;
      this.$emit('switch-capacity', this.selectCapacity, capacity, this.license, isFromTabKeyboard, billingCycle, (newPlanInfo) => {
        this.$nextTick(() => {
          this.updateVisuallyHiddenTextContent(newPlanInfo.billing_cycle, newPlanInfo.capacity);
          if (this.$context.is.zs2ze) {
            this.$tracking.buyInteraction(`zoom events attendees`, `${TrackingBillingCycleKeys[newPlanInfo.billing_cycle]} - ${capacity} attendees/year`);
          } else if ([planTypes.WEBINAR].includes(planType)) {
            this.$tracking.buyInteraction(`${planName}`, `${TrackingBillingCycleKeys[newPlanInfo.billing_cycle]} - ${capacity} attendees${TrackingBillingCycleUnitKeys[newPlanInfo.billing_cycle]}`);
          } else {
            this.$tracking.addOnBuyInteraction(`${trackingPlanKey} attendees`, capacity);
          }
        });
      });
    },
    onDelete() {
      this.deleteVisible && this.$emit('delete-capacity', this.selectedPlanCode);
      if (this.billingCycle) {
        this.$tracking.buyInteraction(`${this.trackingPlanInfo.planName}`, `${this.trackingPlanInfo.buySelection} - remove`);
      } else {
        this.$tracking.buyInteraction(`${this.trackingPlanKey} plan`, `remove`);
      }
    },
    focusSelect() {
      this.$refs.attendeeSelect?.focus();
    },
    toggleLicenseTips(tips) {
      tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
    },
    changeBillingCycle(cycle, _, newPlan) {
      this.billingCycle = cycle;

      // just for webinar
      if (!newPlan?.capacity && this.planType === planTypes.WEBINAR) {
        // change capacity
        const isCapacitySupported =  this.billingCycleList.find(p => {
          const { plan } = p;
          return plan.billing_cycle === cycle;
        });
        // may be never run this code?
        if (!isCapacitySupported) {
          const availablePlans = this.allWebinarPlansList.filter(p => {
            const isSelected = this.getSelectedPlans.find(plan => p.plan_code === plan.plan_code || p.capacity === plan.capacity);
            if (p.billing_cycle === cycle && !isSelected) return true;
            return false;
          }).sort((x, y) => {
            return x.capacity - y.capacity;
          });
          if (availablePlans.length) {
            this.onSwitchCapacity(availablePlans[0].capacity, availablePlans[0].billing_cycle);
          }
        }
      }

      this.$emit('switch-cycle', cycle, this.selectCapacity, newPlan.capacity);
    },
    toggleCapacityTips(tips) {
      if (this.$context?.is.zs2ze) {
        tips && this.$tracking.buyInteraction(`zoom events options`, 'select number of attendees - tooltip');
      } else {
        tips && this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, 'licenses - tooltip');
      }
    },
    trackingAlert(e) {
      const { tagName = "", className, text = "" } = e?.target || {};
      const { alert } = this.firstDisabledCycleAlertItem || {};

      if (
        tagName?.toLowerCase() === "a"
        && className === "opc-go-back__billing"
        && this.showCycleAlert
        && alert
      ) {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${alert} - ${text}`,
        );
      }
    },
    updateVisuallyHiddenTextContent(billingCycle, capacity) {
      this.$nextTick(() => {
        const visuallyHiddenEle = document.querySelector('.visually-hidden');
        if (visuallyHiddenEle && billingCycle && capacity) {
          visuallyHiddenEle.textContent = `Changed to ${BillingCycleKeysMapForAx?.[billingCycle]} ${capacity} attendees`;
        }
      });
    }
  },
  mounted() {
    // fix ax issue
    this.$refs.configCycle?.focus?.();
  },
  watch: {
    cartSelectedPlan: {
      immediate: true,
      handler(plan, oldPlan) {
        if (
          plan?.cartInfo?.selected_number === oldPlan?.cartInfo?.selected_number &&
          plan?.billing_cycle === oldPlan?.billing_cycle
        ) {
          return;
        }
        // sync shopping cart form state to local form state
        this.billingCycle = plan?.billing_cycle ?? '';
        this.license = plan?.cartInfo?.selected_number || 1;
      },
    },
    license: {
      immediate: true,
      handler(newLicense, oldLicense) {
        this.$emit(
          'change-license',
          newLicense,
          oldLicense,
          { capacity: this.selectCapacity, billingCycle: this.billingCycle },
          this._uid,
        );
      },
    },
    maxHosts: {
      immediate: true,
      handler(nv) {
        const oldLicense = this.license;
        if (this.license > nv) {
          this.changeLicense(nv);
          // when auto add enable, decrease of base plan license is no longer limited
          // current plan license will change according to the maxHosts(base plan license)
          // show notification
          const planNameKey = this.currentPlanList.find((plan) => plan.type === this.planType)?.planNameKey;
          showSyncAddonMessage(planNameKey, this.currentBasePlan?.planNameKey);
        }
        this.$emit(
          'change-license',
          this.license,
          oldLicense,
          { capacity: this.selectCapacity, billingCycle: this.billingCycle },
          this._uid,
        );
      },
    },
    // Auto add
    // @jira https://zoomvideo.atlassian.net/browse/OBT-2083
    // watch base plan license change to trigger current plan license change
    syncBaseToSelectedPlanLicense: {
      immediate: false,
      handler(nv) {
        if (!nv) return;
        if (!(isAutoAddable(this.planType))) {
          return;
        }
        // not include one-time plan
        if (this.cartSelectedPlan?.billing_cycle === BILLING_CYCLE.ONE_TIME) return;
        const { baseLicense } = nv;
        if (baseLicense < this.license) {
          this.changeLicense(baseLicense);
          const planNameKey = this.currentPlanList.find((plan) => plan.type === this.planType)?.planNameKey;
          showSyncAddonMessage(planNameKey, this.currentBasePlan?.planNameKey);
        }
      },
    },
    firstDisabledCycleAlertItem: {
      immediate: true,
      handler(item) {
        if (this.showCycleAlert && item?.alert) {
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${item.alert} - impression`,
          );
        }
      },
    }
  },
  created() {
    // A closure (timeId) needs to be generated for each config row instance
    this.updateShoppingCart = _.debounce(this.updateCart, 200);
  },
};
</script>

<style lang='scss' scoped>
@import '@zoom/billing-responsive/styles/responsive.scss';
@import '@/styles/scss-utils.scss';
@import './config-row.scss';

.opc-warning-message {
  display: flex;
  align-items: center;
  background: #FEF7D9;
  color: var(--zoom-color-text-warning, #B36200);

  &__addon {
    margin-top: 16px;
  }
}
</style>
